import React from 'react';
import {Modal, Upload, message, Progress} from 'antd';
import {
  InboxOutlined,
  LoadingOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleOutlined,
  LineOutlined,
  CloudUploadOutlined
} from '@ant-design/icons';
import {useUpload} from '../../hooks/useUpload';
import './index.scss';
import {useUploadStore} from '../../store/uploadStore';

const {Dragger} = Upload;

interface UploadPanelProps {
  parentId: number | string;
  open: boolean;
  onCancel: () => void;
  onAIParseOpen?: (results: any) => void;
  mode?: 'file' | 'folder';
  minimized?: boolean;
  onMinimize?: () => void;
  onRestore?: () => void;
}

export function UploadPanel({
                              parentId,
                              open,
                              onCancel,
                              onAIParseOpen,
                              mode = 'file',
                              minimized,
                              onMinimize,
                              onRestore
                            }: UploadPanelProps) {
  const {hasStartedUpload, setHasStartedUpload, bubbleStatus, setBubbleStatus, fileList, setFileList, updateFileList} = useUploadStore();
  const {upload, uploadMany, uploadFolder, uploading, progress, fileProgressList, resetUploadState, cancelUpload} = useUpload(parentId);

  const isFolderMode = mode === 'folder';

  const totalCount = fileProgressList.length;

  const successCount = fileProgressList.filter(item => item.status === 'success').length;

  const currentUploadingItem = fileProgressList.find(item =>
    item.status === 'uploading' || item.status === 'checking'
  );

  const bubblePercent =
    totalCount <= 1
      ? currentUploadingItem?.percent || progress
      : progress;

  function cleanup() {
    setBubbleStatus(null);
    setFileList([]);
    setHasStartedUpload(false);
    resetUploadState();
    onCancel();
  }

  function finishUpload(status: 'success' | 'error') {
    setBubbleStatus(status);

    if (status === 'error') {
      return;
    }

    const currentMinimized = useUploadStore.getState().minimized;

    if (currentMinimized) {
      setTimeout(() => {
        cleanup();
      }, 3000);

      return;
    }

    cleanup();
  }

  async function handleUpload() {
    if (!fileList.length) {
      void message.warning(isFolderMode ? '请选择文件夹' : '请选择文件');
      return;
    }

    const files = fileList.map(item => item.originFileObj).filter(Boolean);

    if (!files.length) return;

    setHasStartedUpload(true);
    setBubbleStatus('uploading');

    try {
      if (isFolderMode) {
        await uploadFolder(files);

        finishUpload('success');
        return;
      }

      // 由于解析速度比较慢，暂且禁用单文件上传以及之后的打开AI parse 弹窗
      // if (files.length === 1) {
      //   const results = await upload(files[0]);
      //
      //   setFileList([]);
      //   onCancel();
      //
      //   if (!results?.isInstantUpload) {
      //     onAIParseOpen?.(results);
      //   }
      //
      //   return;
      // }

      const result = await uploadMany(files);

      if (result?.cancelled) {
        cleanup();
        return;
      }

      if (result?.failedFiles?.length) {
        setHasStartedUpload(false);

        updateFileList(prev =>
          prev.map(item => {
            const failed = result.failedFiles.some((file: File) =>
              file.name === item.name && file.size === item.size
            );

            return failed
              ? {...item, status: 'error'}
              : item;
          })
        );

        finishUpload('error');
        return;
      }

      finishUpload('success');
    } catch (e) {
      console.log(e);
      setHasStartedUpload(false);

      files.forEach(file => {
        updateAntdFileStatus(file, 'error');
      });

      finishUpload('error');
    }
  }

  function handleCancel() {
    if (uploading) {

      const selectedCount = fileList.length;

      //单个文件
      if (selectedCount <= 1) {
        onMinimize?.();
        return;
      }

      Modal.confirm({
        title: '取消上传?',
        content: '当前文件会继续上传完成，但后续文件将停止上传？',
        okText: '停止上传',
        cancelText: '继续上传',
        okType: 'danger',
        onOk: () => {
          cancelUpload();
          setBubbleStatus(null);
          setHasStartedUpload(false);
          setFileList([]);
          resetUploadState();
          onCancel();
        },
      });

      return;
    }

    setHasStartedUpload(false);
    setFileList([]);
    resetUploadState();
    onCancel();
  }

  function renderUploadStatusIcon(status: string) {
    if (status === 'success') return <CheckCircleFilled className="drive-upload-panel__status-icon drive-upload-panel__status-icon--success"/>;
    if (status === 'error') return <CloseCircleFilled className="drive-upload-panel__status-icon drive-upload-panel__status-icon--error"/>;
    if (status === 'uploading' || status === 'checking') return <LoadingOutlined spin
                                                                                 className="drive-upload-panel__status-icon drive-upload-panel__status-icon--active"/>;
    return <ClockCircleOutlined className="drive-upload-panel__status-icon drive-upload-panel__status-icon--waiting"/>;
  }

  function getCurrentUploadText(fileProgressList: any[], progress: number) {
    const errorFile = fileProgressList.find(item => item.status === 'error');
    if (errorFile) return `上传失败 ${errorFile.name}`;

    const current = fileProgressList.find(item =>
      item.status === 'checking' || item.status === 'uploading'
    );

    if (current?.status === 'checking') return `正在校验 ${current.name}`;
    if (progress >= 100) return `正在解析...`;
    if (current?.status === 'uploading') return `正在上传 ${current.name}`;

    return `准备上传...`;
  }

  function updateAntdFileStatus(targetFile: File, status: 'error' | 'done') {
    updateFileList(prev =>
      prev.map(item => {
        const matched =
          item.name === targetFile.name &&
          item.size === targetFile.size;

        if (!matched) return item;

        return {
          ...item,
          status
        };
      })
    );
  }

  return (
    <>
      <Modal
        className="drive-upload-panel"
        title={
          <div className="drive-upload-panel__header">
        <span>
          {isFolderMode ? '上传文件夹' : '上传文件'}
        </span>

            {hasStartedUpload && (
              <span
                className="drive-upload-panel__minimize"
                onClick={e => {
                  e.stopPropagation();
                  onMinimize?.();
                }}
              >
              <LineOutlined/>
            </span>
            )}
          </div>
        }
        visible={open && !minimized}
        confirmLoading={uploading}
        okText={uploading
          ? progress >= 100
            ? '正在上传...' // 原为正在解析，但单文件禁用临时更改
            : `正在上传`
          : '开始上传'
        }
        cancelText="取消"
        onCancel={handleCancel}
        onOk={handleUpload}
        destroyOnClose={!minimized}
        afterClose={() => {
          if (minimized) return;

          if (uploading) return;

          setFileList([]);
          setHasStartedUpload(false);
          resetUploadState();
        }}
        maskClosable={!uploading}
        keyboard={!uploading}
        width={600}
      >
        <Dragger
          className="drive-upload-panel__dragger"
          multiple
          directory={isFolderMode}
          disabled={uploading}
          showUploadList={{
            showRemoveIcon: !uploading
          }}
          fileList={fileList}
          itemRender={(originNode, file) => {
            if (!hasStartedUpload) {
              return originNode;
            }

            const progressItem = fileProgressList.find(
              item =>
                item.name === file.name &&
                item.size === file.size
            );

            if (!progressItem) {
              return originNode;
            }

            return (
              <div className="drive-upload-panel__upload-item">
                <div className="drive-upload-panel__upload-item-main">
                  <div className="drive-upload-panel__upload-item-left">
                    {renderUploadStatusIcon(progressItem.status)}

                    <span className="drive-upload-panel__upload-item-name">
                    {file.name}
                  </span>
                  </div>

                  {
                    progressItem.status !== 'error' && (
                      <span className="drive-upload-panel__upload-item-percent">
                      {progressItem.percent}%
                    </span>
                    )
                  }
                </div>

                {/*小进度感觉UI有点乱先隐藏，有需要再开启*/}
                {/*<Progress*/}
                {/*  percent={progressItem.percent}*/}
                {/*  size="small"*/}
                {/*  showInfo={false}*/}
                {/*  status={*/}
                {/*    progressItem.status === 'error'*/}
                {/*      ? 'exception'*/}
                {/*      : progressItem.status === 'success'*/}
                {/*        ? 'success'*/}
                {/*        : 'active'*/}
                {/*  }*/}
                {/*/>*/}
              </div>
            );
          }}
          beforeUpload={() => false}
          onChange={info => setFileList(info.fileList)}
          onRemove={file => {
            if (uploading) return false;
            updateFileList(prev => prev.filter(item => item.uid !== file.uid));
          }}
        >
          <p className="drive-upload-panel__icon">
            <InboxOutlined/>
          </p>
          <p className="drive-upload-panel__text">
            {isFolderMode
              ? '点击或将文件夹拖拽至此区域以上传'
              : '点击或将文件拖拽至此区域以上传'}
          </p>
          <p className="drive-upload-panel__hint">
            {isFolderMode
              ? '会保留文件夹层级结构'
              : '支持单次或批量上传'}
          </p>
        </Dragger>

        {/*进度区域*/}
        {hasStartedUpload && (
          <div className="drive-upload-panel__uploading">
            <div className="drive-upload-panel__current">
              {getCurrentUploadText(fileProgressList, progress)}
            </div>

            <div className="drive-upload-panel__total">
              <Progress
                percent={progress}
                status={progress >= 100 ? 'success' : 'active'}
              />
            </div>
          </div>
        )}

        {/*{!!fileList.length && (*/}
        {/*  <div className="drive-upload-panel__summary">*/}
        {/*    已选择 <span>{fileList.length}</span> 项*/}
        {/*  </div>*/}
        {/*)}*/}
      </Modal>

      {minimized && bubbleStatus && (
        <div
          className={`drive-upload-bubble drive-upload-bubble--${bubbleStatus}`}
          onClick={onRestore}
        >
          <Progress
            type="circle"
            width={58}
            percent={bubblePercent}
            status={
              bubbleStatus === 'success'
                ? 'success'
                : bubbleStatus === 'error'
                  ? 'exception'
                  : 'normal'
            }
            format={() => `${successCount}/${totalCount}`}
          />
        </div>
      )}
    </>
  );
}
